import UsersAppService from "../../../application/platform/security/users-app-service";
import BaseAppService from "../../../application/platform/base/base-app-service";
import { ACTION_TYPES, useUsersManagementDispatch, useUsersManagementState } from "./users-management-context";
import { IListUsersRequest } from "../../../model/security/request/list-users-request";
import { ICreateUserRequest, IEntityAccess } from "../../../model/security/request/create-user-request";
import { IUpdateUserRequest } from "../../../model/security/request/update-user-request";
import { useBaseLayoutController } from "../../base-layout/context/base-layout-controller";
import { DefaultDialogProps } from "../../components/default-dialog";
import { PASSWORD_REGEX } from "../../components/password-field";

export const useUsersManagementController = () => {
    const { usersPagedRequest, userForm, isEditing, selectedUser, accessGroups } = useUsersManagementState();
    const usersManagementDispatch = useUsersManagementDispatch();
    const { showBackDrop, showMessage, showDialog } = useBaseLayoutController();

    const loadData = async (request: IListUsersRequest) => {
        if (request?.page !== undefined && request?.pageSize !== undefined) {
            usersManagementDispatch({
                type: ACTION_TYPES.setIsLoadingData,
                payload: true,
            });

            try {
                const data = await UsersAppService.listUsers(request);
                usersManagementDispatch({
                    type: ACTION_TYPES.setUsersPagedResponse,
                    payload: data,
                });
            } catch (error) {
                console.error('Error loading users:', error);
                showMessage({ type: 'error', message: 'Erro ao carregar usuários.' });
            } finally {
                usersManagementDispatch({
                    type: ACTION_TYPES.setIsLoadingData,
                    payload: false,
                });
            }
        }
    };

    const onChangePagination = async (page: number, pageSize: number) => {
        if (usersPagedRequest?.page === page && usersPagedRequest?.pageSize === pageSize) {
            return;
        }

        const request = { ...usersPagedRequest, page, pageSize } as IListUsersRequest;
        usersManagementDispatch({
            type: ACTION_TYPES.setUsersPagedRequest,
            payload: request,
        });

        await loadData(request);
    };

    const handleFilterChange = (e: any) => {
        let email = usersPagedRequest?.email;
        let name = usersPagedRequest?.name;
        let isActive = usersPagedRequest?.isActive;

        if (e.target.name === "input-email") {
            email = e.target.value;
        }
        if (e.target.name === "input-name") {
            name = e.target.value;
        }
        if (e.target.name === "select-status") {
            isActive = e.target.value === "" ? undefined : e.target.value === "true";
        }

        usersManagementDispatch({
            type: ACTION_TYPES.setUsersPagedRequest,
            payload: { ...usersPagedRequest, email, name, isActive },
        });
    };

    const clearFilters = async () => {
        const request = {
            page: 0,
            pageSize: 50,
            email: "",
            name: "",
            isActive: undefined
        } as IListUsersRequest;

        usersManagementDispatch({
            type: ACTION_TYPES.setUsersPagedRequest,
            payload: request,
        });

        await loadData(request);
    };

    const searchUsers = async () => {
        const request = { ...usersPagedRequest, page: 0 };
        usersManagementDispatch({
            type: ACTION_TYPES.setUsersPagedRequest,
            payload: request,
        });
        await loadData(request);
    };

    const loadUserById = async (id: number) => {
        showBackDrop(true);
        usersManagementDispatch({
            type: ACTION_TYPES.setIsLoadingUser,
            payload: true,
        });

        try {
            const user = await UsersAppService.getUserById(id);
            usersManagementDispatch({
                type: ACTION_TYPES.setSelectedUser,
                payload: user,
            });

            const formData = {
                email: user.email,
                password: '',
                accessGroupId: user.accessGroupId,
                name: user.name,
                documentNumber: user.documentNumber,
                isActive: user.isActive,
                personEntityIds: user.personEntityIds,
                personCompanyIds: user.personCompanyIds,
                entityAccesses: user.entityAccesses
            } as ICreateUserRequest;

            usersManagementDispatch({
                type: ACTION_TYPES.setUserForm,
                payload: formData,
            });
        } catch (error) {
            showMessage({ type: 'error', message: 'Erro ao carregar usuário.' });
        } finally {
            showBackDrop(false);
            usersManagementDispatch({
                type: ACTION_TYPES.setIsLoadingUser,
                payload: false,
            });
        }
    };

    const handleFormChange = (field: string, value: any) => {
        usersManagementDispatch({
            type: ACTION_TYPES.setUserForm,
            payload: { ...userForm, [field]: value },
        });
    };

    const openDrawer = (editMode = false, userId?: number) => {
        usersManagementDispatch({
            type: ACTION_TYPES.setIsEditing,
            payload: editMode,
        });

        if (editMode && userId) {
            loadUserById(userId);
        } else {
            // Reseta o formulário
            usersManagementDispatch({
                type: ACTION_TYPES.resetUserForm,
                payload: null,
            });

            // Pré-seleciona o grupo de acesso "Básico" para novos usuários
            const basicGroup = accessGroups.find(
                group => group.name?.toLowerCase() === 'cliente' ||
                    group.description?.toLowerCase().includes('cliente')
            );

            if (basicGroup) {
                // Usa setTimeout para garantir que o reset foi aplicado antes
                setTimeout(() => {
                    usersManagementDispatch({
                        type: ACTION_TYPES.setUserForm,
                        payload: {
                            ...userForm,
                            email: '',
                            password: '',
                            name: '',
                            documentNumber: '',
                            isActive: true,
                            personEntityIds: [],
                            personCompanyIds: [],
                            entityAccesses: [],
                            confirmPassword: '',
                            accessGroupId: basicGroup.accessGroupId
                        },
                    });
                }, 0);
            }
        }

        usersManagementDispatch({
            type: ACTION_TYPES.setIsDrawerOpen,
            payload: true,
        });
    };

    const closeDrawer = () => {
        usersManagementDispatch({
            type: ACTION_TYPES.setIsDrawerOpen,
            payload: false,
        });

        usersManagementDispatch({
            type: ACTION_TYPES.setSelectedUser,
            payload: null,
        });

        usersManagementDispatch({
            type: ACTION_TYPES.resetUserForm,
            payload: null,
        });
    };

    const validateForm = (): boolean => {
        if (!userForm.email || userForm.email.length === 0) {
            showMessage({ type: 'error', message: 'Email é obrigatório.' });
            return false;
        }
        if (!userForm.name || userForm.name.length === 0) {
            showMessage({ type: 'error', message: 'Nome é obrigatório.' });
            return false;
        }

        // Validação de senha (obrigatória apenas na criação)
        if (!isEditing && (!userForm.password || userForm.password.length < 6)) {
            showMessage({ type: 'error', message: 'Senha deve ter no mínimo 6 caracteres.' });
            return false;
        }

        // Validação de complexidade da senha (se senha foi preenchida)
        if (userForm.password && userForm.password.length > 0) {
            if (!PASSWORD_REGEX.test(userForm.password)) {
                showMessage({
                    type: 'error',
                    message: 'Senha deve conter maiúscula, minúscula e caractere especial.'
                });
                return false;
            }
        }

        // Validação de confirmação de senha (apenas na criação)
        if (!isEditing && userForm.password !== userForm.confirmPassword) {
            showMessage({ type: 'error', message: 'As senhas não coincidem.' });
            return false;
        }

        if (!userForm.accessGroupId || userForm.accessGroupId === 0) {
            showMessage({ type: 'error', message: 'Grupo de acesso é obrigatório.' });
            return false;
        }

        // Validação específica para grupo Básico (id 3): deve ter pelo menos uma entidade
        if (userForm.accessGroupId === 3) {
            if (!userForm.entityAccesses || userForm.entityAccesses.length === 0) {
                showMessage({
                    type: 'error',
                    message: 'Usuários do grupo Básico devem ter pelo menos uma entidade vinculada.'
                });
                return false;
            }
        }

        // Validação de entidades de acesso (se preenchidas, devem estar completas)
        if (userForm.entityAccesses && userForm.entityAccesses.length > 0) {
            for (const access of userForm.entityAccesses) {
                if (!access.personEntityId || !access.accessProfileId) {
                    showMessage({
                        type: 'error',
                        message: 'Preencha entidade e perfil de acesso corretamente.'
                    });
                    return false;
                }
            }
        }

        return true;
    };

    const saveUser = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            showBackDrop(true);

            if (isEditing && selectedUser) {
                const updateRequest = { ...userForm, userId: selectedUser.userId } as IUpdateUserRequest;
                await UsersAppService.updateUser(updateRequest);
            } else {
                await UsersAppService.createUser(userForm);
            }

            showMessage({ type: 'success', message: 'Usuário salvo com sucesso.' });
            closeDrawer();
            await loadData(usersPagedRequest);
        } catch (error) {
            showMessage({ type: 'error', message: 'Erro ao salvar usuário.' });
        } finally {
            showBackDrop(false);
        }
    };

    const toggleUserStatus = async (userId: number, currentStatus: boolean) => {
        showDialog({
            open: true,
            message: `Deseja realmente ${currentStatus ? 'desativar' : 'ativar'} este usuário?`,
            onYesClick: async () => {
                try {
                    showBackDrop(true);
                    await UsersAppService.enableDisableUser({
                        userId,
                        isActive: !currentStatus
                    });
                    showMessage({ type: 'success', message: 'Status atualizado com sucesso.' });
                    await loadData(usersPagedRequest);
                } catch (error) {
                    showMessage({ type: 'error', message: 'Erro ao alterar status do usuário.' });
                } finally {
                    showBackDrop(false);
                }
            }
        } as unknown as DefaultDialogProps);
    };

    const resetUserPassword = async (userId: number, userName: string, userEmail?: string) => {
        showDialog({
            open: true,
            message: `Deseja realmente redefinir a senha do usuário "${userName}"? Uma nova senha será gerada.`,
            onYesClick: async () => {
                try {
                    showBackDrop(true);
                    const response = await UsersAppService.resetPassword(userId);
                    showBackDrop(false);

                    // Abre o dialog customizado com as informações da senha
                    usersManagementDispatch({
                        type: ACTION_TYPES.setPasswordResetDialog,
                        payload: {
                            open: true,
                            userName: userName,
                            userEmail: userEmail || '',
                            newPassword: response.newPassword
                        }
                    });

                    // Copia automaticamente para a área de transferência
                    navigator.clipboard.writeText(response.newPassword).catch(() => {
                        console.error('Failed to copy password to clipboard');
                    });
                } catch (error) {
                    showBackDrop(false);
                    showMessage({ type: 'error', message: 'Erro ao redefinir senha do usuário.' });
                }
            }
        } as unknown as DefaultDialogProps);
    };

    const closePasswordResetDialog = () => {
        usersManagementDispatch({
            type: ACTION_TYPES.closePasswordResetDialog,
            payload: null
        });
    };

    const loadLookupData = async () => {
        usersManagementDispatch({
            type: ACTION_TYPES.setIsLoadingLookups,
            payload: true,
        });

        try {
            const [groups, profiles, entities] = await Promise.all([
                BaseAppService.listAccessGroups(),
                BaseAppService.listAccessProfiles(),
                BaseAppService.listClients()
            ]);

            usersManagementDispatch({
                type: ACTION_TYPES.setAccessGroups,
                payload: groups,
            });

            usersManagementDispatch({
                type: ACTION_TYPES.setAccessProfiles,
                payload: profiles,
            });

            usersManagementDispatch({
                type: ACTION_TYPES.setPersonEntities,
                payload: entities,
            });
        } catch (error) {
            console.error('Error loading lookup data:', error);
            showMessage({ type: 'error', message: 'Erro ao carregar dados auxiliares.' });
        } finally {
            usersManagementDispatch({
                type: ACTION_TYPES.setIsLoadingLookups,
                payload: false,
            });
        }
    };

    const handleAddEntityAccess = () => {
        const newAccess: IEntityAccess = {
            personEntityId: 0,
            accessProfileId: 0
        };

        const updatedAccesses = [...(userForm.entityAccesses || []), newAccess];
        handleFormChange('entityAccesses', updatedAccesses);
    };

    const handleRemoveEntityAccess = (index: number) => {
        const updatedAccesses = userForm.entityAccesses?.filter((_, i) => i !== index) || [];
        handleFormChange('entityAccesses', updatedAccesses);
    };

    const handleUpdateEntityAccess = (index: number, field: string, value: any) => {
        const updatedAccesses = [...(userForm.entityAccesses || [])];
        updatedAccesses[index] = {
            ...updatedAccesses[index],
            [field]: value
        };
        handleFormChange('entityAccesses', updatedAccesses);
    };

    return {
        loadData,
        handleFilterChange,
        onChangePagination,
        clearFilters,
        searchUsers,
        loadUserById,
        handleFormChange,
        openDrawer,
        closeDrawer,
        saveUser,
        toggleUserStatus,
        resetUserPassword,
        closePasswordResetDialog,
        loadLookupData,
        handleAddEntityAccess,
        handleRemoveEntityAccess,
        handleUpdateEntityAccess
    };
};
